import { Children, useState, useCallback } from 'react'
import { List, ListSubheader, Collapse, Box } from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'

import { usePermissions } from '../../../hooks'

const STORAGE_KEY = 'admin-menu-collapsed-sections'

function getCollapsedSections() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

function setCollapsedSection(title, collapsed) {
  try {
    const current = getCollapsedSections()
    current[title] = collapsed
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
  } catch {
    // Ignore storage errors
  }
}

/**
 * ProtectedSection component
 * Only displays the section (with header) if at least one child menu item is visible
 * Sections are collapsible with state persisted in localStorage
 *
 * @param {string} title - The section header text
 * @param {React.ReactNode} children - ProtectedMenuItem components
 */
export function ProtectedSection({ title, children }) {
  const { hasAnyPermission, loading } = usePermissions()
  const [isExpanded, setIsExpanded] = useState(() => !getCollapsedSections()[title])

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => {
      const newValue = !prev
      setCollapsedSection(title, !newValue)
      return newValue
    })
  }, [title])

  // Don't render anything while permissions are loading
  if (loading) {
    return null
  }

  // Check if any children would be visible
  const childrenArray = Children.toArray(children)
  const hasVisibleChildren = childrenArray.some((child) => {
    // Check if this is a ProtectedMenuItem with requiredPermissions
    if (child?.props?.requiredPermissions) {
      return hasAnyPermission(child.props.requiredPermissions)
    }
    // If no permissions required, it's visible
    return true
  })

  // Don't render the section if no children are visible
  if (!hasVisibleChildren) {
    return null
  }

  // Render the section with collapsible header
  return (
    <List
      subheader={
        <ListSubheader
          onClick={handleToggle}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            userSelect: 'none',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <span>{title}</span>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>
        </ListSubheader>
      }
    >
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </List>
  )
}
